import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { defer, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import { SocialAuthService } from '@abacritt/angularx-social-login';

import { LoginRequest, LoginResponse, RegisterRequest, SwitchOrgResponse } from '../dtos/auth-dto';
import { GoogleEmailProof, GoogleEmailProofChallenge } from '../dtos/google-auth-dto';
import { HANDLE_AUTH_ERROR_LOCALLY } from '../interceptors/auth-interceptor';
import { User, UserOrganization } from '../domain/user-model';
import { environment } from 'src/environments/environment';
import { HttpDTO, PaginatedHttpDTO } from '../dtos/http-dto';
import { PermissionService } from './permission-service';
import { PostHogService } from './posthog.service';
import { ProvisioningAttempts } from './provisioning-attempts';
import { SerializedRequestQueue } from '@/shared/utils/serialized-request-queue';
import {
  clearLastOrganization,
  currentOrganizationOf,
  storeLastOrganization,
} from '@/shared/utils/organization-route';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;
  private router = inject(Router);
  private socialAuthService = inject(SocialAuthService);
  private permissionService = inject(PermissionService);
  private posthogService = inject(PostHogService);
  private provisioningAttempts = inject(ProvisioningAttempts);
  private identifiedUserId: string | null = null;
  private authenticationVersion = 0;
  private sessionVersion = 0;

  isAuthenticated = signal<boolean>(!!localStorage.getItem('accessToken'));
  currentUser = signal<User | null>(null);
  private readonly organizationSwitches = signal(0);
  readonly isSwitchingOrganization = computed(() => this.organizationSwitches() > 0);
  private lastSwitchToken: string | null = null;
  private lastSwitchUserId: string | null = null;
  private readonly organizationSwitchQueue = new SerializedRequestQueue<HttpDTO<User>>(
    (count) => this.organizationSwitches.set(count),
    (error) => {
      const status = (error as HttpErrorResponse)?.status ?? 0;
      return status >= 400 && status < 500 && status !== 401 && status !== 429;
    },
  );

  login(credentials: LoginRequest) {
    return this.sessionRequest(() =>
      this.http.post<HttpDTO<LoginResponse>>(`${this.apiUrl}/auth/login`, credentials),
    ).pipe(
      tap((response) => {
        localStorage.setItem('accessToken', response.data.access_token);
        storeLastOrganization(currentOrganizationOf(response.data.user));
        this.isAuthenticated.set(true);
        this.identifyUser(response.data.user);
      }),
    );
  }

  loginWithGoogle(idToken: string) {
    return this.sessionRequest(() =>
      this.http.post<HttpDTO<LoginResponse>>(
        `${this.apiUrl}/auth/google`,
        {
          id_token: idToken,
        },
        {
          context: this.googleRequestContext(),
        },
      ),
    ).pipe(tap((response) => this.acceptGoogleSession(response)));
  }

  requestGoogleEmailProof(idToken: string) {
    return this.http.post<HttpDTO<GoogleEmailProofChallenge>>(
      `${this.apiUrl}/auth/google/email-proof`,
      { id_token: idToken },
      { context: this.googleRequestContext() },
    );
  }

  completeGoogleEmailProof(idToken: string, proof: GoogleEmailProof) {
    return this.sessionRequest(() =>
      this.http.post<HttpDTO<LoginResponse>>(
        `${this.apiUrl}/auth/google/email-proof/complete`,
        {
          id_token: idToken,
          ...proof,
        },
        {
          context: this.googleRequestContext(),
        },
      ),
    ).pipe(tap((response) => this.acceptGoogleSession(response)));
  }

  linkGoogle(idToken: string, currentPassword: string, emailProof?: GoogleEmailProof) {
    return this.http.post<void>(
      `${this.apiUrl}/users/me/google`,
      {
        id_token: idToken,
        current_password: currentPassword,
        ...(emailProof ? { email_proof: emailProof } : {}),
      },
      {
        context: this.googleRequestContext(),
      },
    );
  }

  /** Probe the Hubnari bearer after an ambiguous Google-link 401. Uses normal 401 handling. */
  checkCurrentSession() {
    return this.http.get<unknown>(`${this.apiUrl}/users/me`);
  }

  private googleRequestContext(): HttpContext {
    return new HttpContext().set(HANDLE_AUTH_ERROR_LOCALLY, true);
  }

  private sessionRequest(request: () => Observable<HttpDTO<LoginResponse>>) {
    return defer(() => {
      const version = ++this.authenticationVersion;
      const sessionVersion = this.sessionVersion;
      const token = localStorage.getItem('accessToken');
      const ownsAttempt = () =>
        version === this.authenticationVersion && sessionVersion === this.sessionVersion;
      const isCurrentAttempt = () => ownsAttempt() && localStorage.getItem('accessToken') === token;

      return request().pipe(
        catchError((error: HttpErrorResponse) => {
          const currentToken = localStorage.getItem('accessToken');
          // The existing interceptor may clear this attempt's token on its own 401.
          return ownsAttempt() &&
            (currentToken === token || (error.status === 401 && !currentToken))
            ? throwError(() => error)
            : EMPTY;
        }),
        filter(isCurrentAttempt),
        tap(() => this.sessionVersion++),
      );
    });
  }

  private acceptGoogleSession(response: HttpDTO<LoginResponse>): void {
    localStorage.setItem('accessToken', response.data.access_token);
    storeLastOrganization(currentOrganizationOf(response.data.user));
    this.isAuthenticated.set(true);
    this.identifyUser(response.data.user);
  }

  logout() {
    this.sessionVersion++;
    this.posthogService.posthog?.reset();
    this.identifiedUserId = null;
    this.provisioningAttempts.clearUser();
    localStorage.removeItem('accessToken');
    clearLastOrganization();
    this.currentUser.set(null);
    this.permissionService.reset();

    void this.socialAuthService.signOut().catch((err) => {
      // The SDK rejects with strings and does not expose its no-session constants.
      if (
        err !== 'Not logged in' &&
        err !== 'Login providers not ready yet. Are there errors on your console?'
      ) {
        console.warn('Erro ao deslogar do Google', err);
      }
    });

    this.isAuthenticated.set(false);
    this.router.navigate(['/']);
  }

  register(req: RegisterRequest) {
    return this.sessionRequest(() =>
      this.http.post<HttpDTO<LoginResponse>>(`${this.apiUrl}/auth/register`, req),
    ).pipe(
      tap((response) => {
        localStorage.setItem('accessToken', response.data.access_token);
        this.isAuthenticated.set(true);
        this.identifyUser(response.data.user);
      }),
    );
  }

  recoverPassword(email: string) {
    return this.http.post(`${this.apiUrl}/auth/recovery`, { email });
  }

  resetPassword(code: string, email: string, password: string) {
    return this.http.post(`${this.apiUrl}/auth/reset`, { code, email, password });
  }

  getMe() {
    return defer(() => {
      const token = localStorage.getItem('accessToken');
      const isCurrentSession = () => !!token && localStorage.getItem('accessToken') === token;
      return this.http.get<HttpDTO<User>>(`${this.apiUrl}/users/me`).pipe(
        filter(isCurrentSession),
        switchMap((res) => {
          this.currentUser.set(res.data);
          storeLastOrganization(currentOrganizationOf(res.data));
          this.identifyUser(res.data);

          return this.permissionService.syncFromUser(res.data, isCurrentSession).pipe(
            filter(isCurrentSession),
            map(() => res),
          );
        }),
      );
    });
  }

  private identifyUser(user: User): void {
    this.provisioningAttempts.activateUser(user.id);
    if (this.identifiedUserId === user.id) {
      return;
    }

    this.posthogService.posthog?.identify(user.id, {
      email: user.email,
      name: user.name,
      role: user.role?.name,
      is_superuser: user.superuser,
      organization_id: user.current_organization_id,
    });
    this.identifiedUserId = user.id;
  }

  getOrganizations(page: number, limit: number, search: string) {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('items_per_page', limit.toString());

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<PaginatedHttpDTO<UserOrganization[]>>(`${this.apiUrl}/organizations`, {
      params,
      observe: 'response',
    });
  }

  waitForOrganizationSwitch() {
    return this.organizationSwitchQueue.whenIdle();
  }

  /** Serialize switches, retaining only the newest pending target/caller. */
  switchOrganization(organizationRef: string) {
    const token = localStorage.getItem('accessToken');
    const userId = this.currentUser()?.id;
    return this.organizationSwitchQueue.run(() =>
      this.performOrganizationSwitch(organizationRef, token, userId),
    );
  }

  private performOrganizationSwitch(
    organizationRef: string,
    requestedToken: string | null,
    requestedUserId: string | undefined,
  ) {
    const currentToken = localStorage.getItem('accessToken');
    if (
      !requestedToken ||
      !currentToken ||
      (currentToken !== requestedToken &&
        (!requestedUserId ||
          currentToken !== this.lastSwitchToken ||
          requestedUserId !== this.lastSwitchUserId))
    )
      return EMPTY;

    let ownedToken = currentToken;
    let acceptedToken = false;
    const isCurrentSession = () =>
      !!ownedToken && localStorage.getItem('accessToken') === ownedToken;
    return this.http
      .post<
        HttpDTO<SwitchOrgResponse>
      >(`${this.apiUrl}/organizations/${encodeURIComponent(organizationRef)}/switch`, {})
      .pipe(
        filter(isCurrentSession),
        tap((response) => {
          ownedToken = response.data.access_token;
          localStorage.setItem('accessToken', ownedToken);
          acceptedToken = true;
          this.lastSwitchToken = ownedToken;
          this.lastSwitchUserId = response.data.user.id;
          this.currentUser.set(null);
          this.permissionService.reset();
          storeLastOrganization(currentOrganizationOf(response.data.user));
        }),
        switchMap(() => this.getMe()),
        filter(isCurrentSession),
        catchError((error: HttpErrorResponse) => {
          const current = localStorage.getItem('accessToken');
          if ((acceptedToken && current === ownedToken) || (error.status === 401 && !current)) {
            this.currentUser.set(null);
            this.permissionService.reset();
          }
          throw error;
        }),
      );
  }
}
